import { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { Pencil } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useRequirementsStore } from '@/frontend/store/RequirementsStore';
import { useGlobalStore } from '@/frontend/store/GlobalStore';
import { VerificationCriteriaTask } from '@/backend/models/verificationCriteriaTask';
import { VerificationCriteriaTaskCompletionStatus } from '@/backend/access/verificationCriteriaTask/types';
import { I18n } from '@/i18n/I18n';
import { UserAssignmentSelect } from '../UserAssignmentSelect';
import './ProjectReadyTaskList.css';

interface ProjectReadyTaskListProps {
  projectId: string;
}

export function ProjectReadyTaskCard({ projectId }: ProjectReadyTaskListProps) {
  const tasks = useRequirementsStore(useShallow(state => state.verificationCriteriaTasks));
  const fetchReadyTasks = useRequirementsStore(state => state.fetchReadyVerificationCriteriaTasksByProjectId);
  const fetchUsers = useGlobalStore(state => state.fetchUsers);
  const [visibleTaskIds, setVisibleTaskIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    void (async () => {
      const fetchedTasks = await fetchReadyTasks(projectId);
      const pendingIds = fetchedTasks
        .filter(task => task.completionStatus === VerificationCriteriaTaskCompletionStatus.PENDING)
        .map(task => task.id);
      setVisibleTaskIds(new Set(pendingIds));
    })();
  }, [projectId, fetchReadyTasks]);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  const visibleTasks = tasks.filter(task => visibleTaskIds.has(task.id));

  return (
    <div className="list-card" style={{ width: '340px' }}>
      <div className="list-card-title">
          {I18n.get('taskList', { count: String(visibleTasks.length) })}
      </div>
      <ProjectReadyTaskList tasks={visibleTasks} />
    </div>
  );
}

interface ProjectReadyTaskListInnerProps {
  tasks: VerificationCriteriaTask[];
}

export function ProjectReadyTaskList({ tasks }: ProjectReadyTaskListInnerProps) {
  return (
    <div className="list-card-block">
      {tasks.map(task => (
        <ProjectReadyTaskRow key={task.id} task={task} />
      ))}
    </div>
  );
}

function ProjectReadyTaskRow({ task }: { task: VerificationCriteriaTask }) {
  const updateTask = useRequirementsStore(state => state.updateVerificationCriteriaTask);
  const assignedUser = useGlobalStore(useShallow(state => state.users.find(u => u.id === task.assignedUserId)));
  const [showEditModal, setShowEditModal] = useState(false);
  const isCompleted = task.completionStatus === VerificationCriteriaTaskCompletionStatus.COMPLETED;

  const toggleCompletion = () => {
    void updateTask({
      ...task,
      completionStatus: isCompleted
        ? VerificationCriteriaTaskCompletionStatus.PENDING
        : VerificationCriteriaTaskCompletionStatus.COMPLETED,
    });
  };

  return (
    <div className={`project-ready-task-row ${isCompleted ? 'is-completed' : ''}`}>
      <div className='d-flex gap-2'>
        <Form.Check
          checked={isCompleted}
          onChange={toggleCompletion}
          aria-label={I18n.get('markTaskComplete')}
        />
        <div>
          <div className="project-ready-task-description">{task.description}</div>
          {assignedUser && (
            <div className="project-ready-task-assigned-user">{assignedUser.email}</div>
          )}
        </div>
      </div>
      {!isCompleted && (
        <button
          type="button"
          className="fusa-btn-small-with-icon btn-neutral-light project-ready-task-edit-btn"
          onClick={() => setShowEditModal(true)}
          aria-label={I18n.get('editTask')}
        >
          <Pencil size={16} />
        </button>
      )}
      {showEditModal && (
        <EditTaskModal task={task} onClose={() => setShowEditModal(false)} />
      )}
    </div>
  );
}

function EditTaskModal({ task, onClose }: { task: VerificationCriteriaTask; onClose: () => void }) {
  const updateTask = useRequirementsStore(state => state.updateVerificationCriteriaTask);
  const [description, setDescription] = useState(task.description);
  const [assignedUserId, setAssignedUserId] = useState<string | null>(task.assignedUserId);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    await updateTask({ ...task, description, assignedUserId });
    setIsSaving(false);
    onClose();
  };

  return (
    <Modal show onHide={onClose}>
      <Modal.Body>
        <Modal.Title className="pb-3">{I18n.get('editTask')}</Modal.Title>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label className="form-label-neutral">{I18n.get('taskDescription')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="form-textarea-neutral"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              aria-label={I18n.get('taskDescription')}
            />
          </Form.Group>
          <UserAssignmentSelect
            assignedUserId={assignedUserId}
            onAssign={(userId) => setAssignedUserId(userId)}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button className='fusa-btn-medium btn-blue-light' onClick={onClose} disabled={isSaving}>
          {I18n.get('cancel')}
        </Button>
        <Button
          className='fusa-btn-medium fusa-btn-primary'
          onClick={() => void handleSave()}
          disabled={isSaving || description.trim().length === 0}
        >
          {isSaving ? I18n.get('saving') : I18n.get('save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
